import { FC, AnchorHTMLAttributes, ButtonHTMLAttributes } from 'react'
import Link from 'next/link'
import classNames from 'classnames'
import { IconChevronRight } from 'icons'
import Typography from 'components/Typography'
import { splitAnchorLinkProps, TAnchorElementProps } from 'components/Anchor'
import styles from './MenuIndicator.module.scss'

type TButtonAttributes = ButtonHTMLAttributes<HTMLButtonElement>
type TAnchorAttributes = AnchorHTMLAttributes<HTMLAnchorElement>

type TConditionalProps =
  | ({ element?: 'a' } & TAnchorAttributes)
  | ({ element?: 'button' } & TButtonAttributes)

type THeroProps = {
  value?: number
  label: string
}

type TItem = {
  value: number
  label: string
}

export type TProps = {
  hero: THeroProps
  items?: TItem[]
  content?: string
} & TConditionalProps

const MenuIndicator: FC<TProps> = ({
  element = 'button',
  hero,
  items = [],
  content,
  children,
  ...rest
}) => {
  const hasItems = items?.length > 0
  const hasContent = Boolean(content)

  const getContent = () => (
    <>
      <div className={styles.ecMenuIndicatorContainer}>
        <div className={styles.ecMenuIndicatorHero}>
          {hero.value?.toString() && (
            <Typography
              className={styles.ecMenuIndicatorHeroValue}
              variant="DisplaySmall"
              bold
            >
              {hero.value}
            </Typography>
          )}
          <Typography variant="ParagraphBig" bold>
            {hero.label}
          </Typography>

          <div className={styles.ecMenuIndicatorIconWrapper}>
            <IconChevronRight
              title="Ícone: seta"
              color="grey900"
              width={32}
              height={32}
            />
          </div>
        </div>

        {(hasItems || hasContent) && (
          <div className={styles.ecMenuIndicatorContent}>
            {hasItems && (
              <ul className={styles.ecMenuIndicatorList}>
                {items.map((item) => (
                  <li
                    key={item.label}
                    className={styles.ecMenuIndicatorListItem}
                  >
                    <Typography variant="TitleSmall" bold>
                      {item.value}
                    </Typography>
                    <Typography
                      className={styles.ecMenuIndicatorListItemLabel}
                      variant="Label"
                      bold
                    >
                      {item.label}
                    </Typography>
                  </li>
                ))}
              </ul>
            )}

            {hasContent && (
              <Typography
                variant="ParagraphSmall"
                className={styles.ecMenuIndicatorDescription}
              >
                {content}
              </Typography>
            )}
          </div>
        )}
      </div>

      {children && (
        <div className={styles.ecMenuIndicatorChildren}>{children}</div>
      )}
    </>
  )

  const className = classNames(styles.ecMenuIndicator)

  if (element === 'a') {
    const { linkProps, anchorProps } = splitAnchorLinkProps({
      ...(rest as TAnchorElementProps),
      className,
    })

    return (
      <Link {...linkProps} {...anchorProps}>
        {getContent()}
      </Link>
    )
  }

  const buttonProps = {
    ...(rest as TButtonAttributes),
    className,
  }

  return (
    <button type="button" {...buttonProps}>
      {getContent()}
    </button>
  )
}

export default MenuIndicator
